import type { TaskGeometry, MarkerShape, Coordinates } from '../../types';
import type {
  GeometryResponseDto,
  GeometryMapResponseDto,
} from '@/types/api/responses/geometry';
import {
  GeometryType,
  LineType,
  LineStyle,
  ShapeType,
  FillType,
} from '../../types/geometry';

/**
 * Capitalizes the first letter of a string value.
 * Handles backward compatibility for DB records with lowercase enum values
 * (e.g., "marker" → "Marker", "solid" → "Solid").
 */
function capitalizeFirst(value: string | undefined | null): string | undefined {
  if (value == null || value === '') return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Legacy fill type values with irregular casing that capitalizeFirst cannot handle.
 * "semi-transparent" → "SemiTransparent"
 */
const FILL_TYPE_LEGACY_MAP: Partial<Record<string, string>> = {
  'semi-transparent': 'SemiTransparent',
};

/**
 * Normalizes a FillType value from API/DB, handling the special case
 * of "semi-transparent" → "SemiTransparent".
 */
function normalizeFillType(
  value: string | undefined | null
): string | undefined {
  if (value == null || value === '') return undefined;
  return FILL_TYPE_LEGACY_MAP[value] ?? capitalizeFirst(value);
}

/**
 * Derives the center position from geoData coordinates.
 * Uses calculatedCenter from backend only as fallback.
 */
function deriveCenter(
  geo: GeometryResponseDto | GeometryMapResponseDto
): Coordinates {
  const data = geo.geoData;

  if (data.coordinates) {
    return { lat: data.coordinates.lat, lng: data.coordinates.lng };
  }

  const points = data.linePoints ?? data.shapePoints;
  if (points?.length) {
    const sum = points.reduce(
      (acc, pt) => ({ lat: acc.lat + pt.lat, lng: acc.lng + pt.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / points.length, lng: sum.lng / points.length };
  }

  return { lat: geo.calculatedCenter.lat, lng: geo.calculatedCenter.lng };
}

/**
 * Maps a geometry API response to a frontend TaskGeometry.
 * Handles both full GeometryResponseDto and minimal GeometryMapResponseDto.
 */
export function mapGeometryFromApi(
  geo: GeometryResponseDto | GeometryMapResponseDto
): TaskGeometry {
  const fullResponse = 'taskId' in geo ? (geo as GeometryResponseDto) : null;

  return {
    id: geo.id,
    type: capitalizeFirst(geo.type) as GeometryType,
    name: fullResponse?.name ?? '',
    description: fullResponse?.description ?? undefined,
    calculatedCenter: deriveCenter(geo),
    // Marker properties
    markerShape: capitalizeFirst(geo.geoData.markerShape) as
      | MarkerShape
      | undefined,
    markerColor: geo.geoData.markerColor,
    markerIcon: geo.geoData.markerIcon,
    // Line properties
    linePoints: geo.geoData.linePoints?.map((point) => ({
      lat: point.lat,
      lng: point.lng,
    })),
    lineType: capitalizeFirst(geo.geoData.lineType) as LineType | undefined,
    lineStyle: capitalizeFirst(geo.geoData.lineStyle) as LineStyle | undefined,
    lineWidth: geo.geoData.lineWidth,
    lineColor: geo.geoData.lineColor,
    parentIndices: geo.geoData.parentIndices,
    // Shape properties
    shapeType: capitalizeFirst(geo.geoData.shapeType) as ShapeType | undefined,
    shapePoints: geo.geoData.shapePoints?.map((point) => ({
      lat: point.lat,
      lng: point.lng,
    })),
    shapeFill: normalizeFillType(geo.geoData.shapeFill) as FillType | undefined,
    shapeFillColor: geo.geoData.shapeFillColor,
    shapeBorderStyle: capitalizeFirst(geo.geoData.shapeBorderStyle) as
      | LineStyle
      | undefined,
    shapeBorderWidth: geo.geoData.shapeBorderWidth,
    shapeBorderColor: geo.geoData.shapeBorderColor,
    // Center marker
    centerMarker: geo.geoData.centerMarker
      ? {
          visible: geo.geoData.centerMarker.visible,
          shape: capitalizeFirst(geo.geoData.centerMarker.shape) as MarkerShape,
          color: geo.geoData.centerMarker.color,
          icon: geo.geoData.centerMarker.icon,
        }
      : undefined,
  };
}
